import { IFormStep } from "./selectorFormSteps";

export type FormWizardPageKind = "top" | "sub";

export interface IFormWizardPage {
  key: string;
  title: string;
  /**
   * "top" pages get their own card in the nav rail (the first identity page
   * and the final sign-and-date page); consecutive "sub" pages group into the
   * rail's single "Form sections" card as dot sub-items.
   */
  kind: FormWizardPageKind;
  /** The wizard step rendered on this page. The identity page carries none. */
  step?: IFormStep;
  hasWarning: boolean;
}

/**
 * Maps the form's wizard steps to ordered navigation PAGES:
 *
 *   [ identity page? , ...section sub-pages , sign page ]
 *
 * - `intro`, when given, becomes the FIRST top-level page (the merged
 *   identity/pre-filled card; the caller passes it only when the form has
 *   pre-filled read-only content).
 * - The certification step — identified generically as the LAST step carrying
 *   a SignatureBlock-type field — becomes a top-level page of its own.
 * - Every other step is a sub-page (a "Form sections" sub-item in the rail).
 *
 * Step order is preserved; navigation walks the returned array in order.
 */
export const selectorWizardPages = (options: {
  steps: IFormStep[];
  intro?: { key: string; title: string; hasWarning?: boolean };
}): IFormWizardPage[] => {
  const { steps, intro } = options;

  let signStepIndex = -1;
  steps.forEach((step, index) => {
    const hasSignature = step.sections.some(section =>
      section.fields.some(field => field.type === "SignatureBlock")
    );
    if (hasSignature) signStepIndex = index;
  });

  const pages: IFormWizardPage[] = [];
  if (intro) {
    pages.push({
      key: intro.key,
      title: intro.title,
      kind: "top",
      hasWarning: Boolean(intro.hasWarning)
    });
  }
  steps.forEach((step, index) => {
    pages.push({
      key: step.key,
      title: step.title,
      kind: index === signStepIndex ? "top" : "sub",
      step,
      hasWarning: step.hasWarning
    });
  });

  return pages;
};
